import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Spin } from 'antd';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  RefreshCw,
} from 'lucide-react';
import {
  useLazyGetAttachmentGalleryFileQuery,
  useLazyGetTravelAttachmentQuery,
  useLazyGetTravelRequestAttachmentFileQuery,
} from '../../../store';
import { ITravelAttachment } from '../../../types';
import { downloadAttachment } from '../../../utils/fileUtils';
import { useNotification } from '../../../contexts';
import { TRAVEL_REQUEST_LINE_ITEM_SECTION_LABEL_MAP } from '../../../pages/TravelRequest/constants/travelreq.constants';

interface LineItemAttachmentCarouselProps {
  attachments: ITravelAttachment[];
  isOpen: boolean;
  onClose: () => void;
  travelRequestId: number;
  requestNo?: string;
  isMetadataLoading?: boolean;
  metadataError?: string | null;
  onRetry?: () => void;
}

const getFileName = (attachment?: ITravelAttachment): string =>
  attachment?.fileName || attachment?.file?.name || 'Attachment';

const getFileExtension = (attachment?: ITravelAttachment): string =>
  getFileName(attachment).split('.').pop()?.toLowerCase() || '';

const isImageAttachment = (
  attachment: ITravelAttachment | undefined,
  mimeType: string,
): boolean =>
  mimeType.startsWith('image/') ||
  ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(
    getFileExtension(attachment),
  );

const isPdfAttachment = (
  attachment: ITravelAttachment | undefined,
  mimeType: string,
): boolean =>
  mimeType === 'application/pdf' || getFileExtension(attachment) === 'pdf';

const LineItemAttachmentCarousel = ({
  attachments,
  isOpen,
  onClose,
  travelRequestId,
  requestNo,
  isMetadataLoading = false,
  metadataError = null,
  onRetry,
}: LineItemAttachmentCarouselProps) => {
  const visibleAttachments = useMemo(
    () => attachments.filter(attachment => !attachment.deleteFlag),
    [attachments],
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewMimeType, setPreviewMimeType] = useState('');
  const [previewError, setPreviewError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [getAttachment, { isFetching }] = useLazyGetTravelAttachmentQuery();
  const [getRequestAttachment, { isFetching: isRequestAttachmentFetching }] =
    useLazyGetTravelRequestAttachmentFileQuery();
  const [getGalleryAttachment, { isFetching: isGalleryFetching }] =
    useLazyGetAttachmentGalleryFileQuery();
  const { showNotification } = useNotification();
  const activeAttachment = visibleAttachments[activeIndex];

  useEffect(() => {
    if (!isOpen) setActiveIndex(0);
  }, [isOpen]);

  useEffect(() => {
    if (activeIndex < visibleAttachments.length) return;
    setActiveIndex(Math.max(visibleAttachments.length - 1, 0));
  }, [activeIndex, visibleAttachments.length]);

  useEffect(() => {
    let disposed = false;
    let objectUrl: string | null = null;

    const loadPreview = async () => {
      setPreviewUrl(null);
      setPreviewMimeType('');
      setPreviewError(false);
      if (!isOpen || !activeAttachment || isMetadataLoading) return;

      try {
        const blob =
          activeAttachment.readSource && activeAttachment.readAttachmentId
            ? await getRequestAttachment({
                travelRequestId,
                source: activeAttachment.readSource,
                attachmentId: activeAttachment.readAttachmentId,
              }).unwrap()
            : activeAttachment.galleryFileId
              ? await getGalleryAttachment(
                  activeAttachment.galleryFileId,
                ).unwrap()
              : activeAttachment.id !== undefined &&
                  isNaN(+activeAttachment.id)
                ? activeAttachment.file
                : await getAttachment({
                    travelRequestId,
                    fileName: activeAttachment.fileName,
                  }).unwrap();

        if (!blob) throw new Error('Attachment file is unavailable');
        objectUrl = URL.createObjectURL(blob);
        if (!disposed) {
          setPreviewMimeType(blob.type || activeAttachment.mimeType || '');
          setPreviewUrl(objectUrl);
        }
      } catch {
        if (!disposed) setPreviewError(true);
      }
    };

    void loadPreview();
    return () => {
      disposed = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    activeAttachment,
    getAttachment,
    getGalleryAttachment,
    getRequestAttachment,
    isOpen,
    isMetadataLoading,
    travelRequestId,
  ]);

  const move = useCallback(
    (direction: -1 | 1) => {
      if (visibleAttachments.length < 2) return;
      setActiveIndex(
        current =>
          (current + direction + visibleAttachments.length) %
          visibleAttachments.length,
      );
    },
    [visibleAttachments.length],
  );

  useEffect(() => {
    if (!isOpen || visibleAttachments.length < 2) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') move(-1);
      if (event.key === 'ArrowRight') move(1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, move, visibleAttachments.length]);

  const handleDownload = async () => {
    if (!activeAttachment || isDownloading) return;
    setIsDownloading(true);
    const succeeded = await downloadAttachment(
      activeAttachment,
      getAttachment,
      travelRequestId,
      getGalleryAttachment,
      getRequestAttachment,
    );
    setIsDownloading(false);
    if (!succeeded) {
      showNotification(
        'error',
        'Download failed',
        'The attachment could not be downloaded. Please try again.',
      );
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      width="min(1040px, 94vw)"
      destroyOnClose
      title={
        <div className="flex min-w-0 items-center justify-between gap-3 pr-8">
          <div className="min-w-0">
            {requestNo && (
              <p className="truncate text-xs font-medium text-indigo-600">
                {requestNo}
              </p>
            )}
            <p className="truncate text-sm font-semibold text-slate-900">
              {isMetadataLoading
                ? 'Loading Attachments'
                : getFileName(activeAttachment)}
            </p>
          </div>
          <span className="shrink-0 text-xs font-normal text-slate-500">
            {isMetadataLoading
              ? 'Preparing preview'
              : visibleAttachments.length
              ? `${activeIndex + 1} of ${visibleAttachments.length}`
              : 'No attachments'}
          </span>
        </div>
      }
      footer={
        activeAttachment ? (
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-left text-xs text-slate-500">
              <span className="mr-2 inline-flex rounded-full bg-indigo-50 px-2 py-0.5 font-medium text-indigo-700">
                {TRAVEL_REQUEST_LINE_ITEM_SECTION_LABEL_MAP[
                  activeAttachment.section
                ] || 'Attachment'}
              </span>
              {activeAttachment.documentType || 'Supporting document'}
              {activeAttachment.remarks ? ` · ${activeAttachment.remarks}` : ''}
            </p>
            <Button
              icon={<Download className="size-4" />}
              loading={isDownloading}
              onClick={() => void handleDownload()}
            >
              Download
            </Button>
          </div>
        ) : null
      }
    >
      <div className="relative flex h-[min(66dvh,680px)] items-center justify-center overflow-hidden rounded-xl bg-slate-950 p-3">
        {isMetadataLoading ? (
          <div className="w-full max-w-lg animate-pulse space-y-4 px-8">
            <div className="mx-auto h-12 w-12 rounded-xl bg-slate-800" />
            <div className="mx-auto h-4 w-48 rounded bg-slate-800" />
            <div className="mx-auto h-3 w-64 max-w-full rounded bg-slate-800" />
          </div>
        ) : metadataError ? (
          <div className="max-w-sm text-center text-white">
            <FileText className="mx-auto size-10 text-rose-300" />
            <p className="mt-3 text-sm font-semibold">
              Attachments Could Not Be Loaded
            </p>
            <p className="mt-1 text-xs leading-5 text-slate-300">
              {metadataError}
            </p>
            {onRetry && (
              <Button
                className="mt-4"
                icon={<RefreshCw className="size-4" />}
                onClick={onRetry}
              >
                Retry
              </Button>
            )}
          </div>
        ) : isFetching ||
          isGalleryFetching ||
          isRequestAttachmentFetching ? (
          <Spin size="large" />
        ) : previewUrl &&
          isImageAttachment(activeAttachment, previewMimeType) ? (
          <img
            src={previewUrl}
            alt={getFileName(activeAttachment)}
            className="max-h-full max-w-full object-contain"
          />
        ) : previewUrl && isPdfAttachment(activeAttachment, previewMimeType) ? (
          <iframe
            src={`${previewUrl}#toolbar=0`}
            title={getFileName(activeAttachment)}
            className="h-full w-full rounded-lg bg-white"
          />
        ) : (
          <div className="max-w-sm text-center text-white">
            <FileText className="mx-auto size-10 text-slate-300" />
            <p className="mt-3 text-sm font-semibold">
              {!activeAttachment
                ? 'No Active Attachments Found'
                : previewError
                ? 'Preview Could Not Be Loaded'
                : 'Preview Not Available'}
            </p>
            <p className="mt-1 text-xs leading-5 text-slate-300">
              {!activeAttachment
                ? 'The request does not currently have any active files to preview.'
                : 'Download this file to open it in its native application.'}
            </p>
          </div>
        )}

        {visibleAttachments.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous attachment"
              onClick={() => move(-1)}
              className="absolute left-3 grid size-9 place-items-center rounded-full bg-white/90 text-slate-800 shadow-lg transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
            >
              <ChevronLeft className="size-5" />
            </button>
            <button
              type="button"
              aria-label="Next attachment"
              onClick={() => move(1)}
              className="absolute right-3 grid size-9 place-items-center rounded-full bg-white/90 text-slate-800 shadow-lg transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
            >
              <ChevronRight className="size-5" />
            </button>
          </>
        )}
      </div>

      {visibleAttachments.length > 1 && (
        <div
          className="mt-3 flex gap-2 overflow-x-auto pb-1"
          aria-label="Attachment list"
        >
          {visibleAttachments.map((attachment, index) => (
            <button
              key={String(
                attachment.id ?? `${getFileName(attachment)}-${index}`,
              )}
              type="button"
              aria-current={index === activeIndex}
              onClick={() => setActiveIndex(index)}
              className={`min-w-0 max-w-52 shrink-0 truncate rounded-lg border px-3 py-2 text-left text-xs transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${
                index === activeIndex
                  ? 'border-indigo-300 bg-indigo-50 font-semibold text-indigo-800'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              }`}
              title={getFileName(attachment)}
            >
              {getFileName(attachment)}
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default LineItemAttachmentCarousel;
